import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError, downloadFile } from "@/lib/api";
import { confirmIfFarDate } from "@/lib/dateGuard";
import { formatBRDate } from "@/lib/formatters";
import { confirm } from "@/lib/confirm";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Trash2 } from "lucide-react";
import { toast } from "sonner";

import FilteredMapList from "./filteredMap/FilteredMapList";
import MapHeaderActions from "./filteredMap/MapHeaderActions";
import {
  PendingTeamPromotions,
  PendingProcedureVariations,
  AmbiguousMatches,
  PendingSurgeons,
} from "./filteredMap/Banners";
import DiscardedRowsSection from "./filteredMap/DiscardedRowsSection";
import SuspensionDialog from "./filteredMap/SuspensionDialog";
import ManualRowDialog from "./filteredMap/ManualRowDialog";
import MetaEditDialog from "./filteredMap/MetaEditDialog";

const PERIOD_LABEL = { manha: "MANHÃ", tarde: "TARDE", noite: "NOITE" };

/** Loose check: does this raw procedure name correspond to any catalog entry?
 * Used to highlight rows where the AI couldn't match — perfect candidates
 * for the "Pro Bono" flag (per business rule). */
function _normalize(s) {
  return (s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}
function procedureExistsInCatalog(rawName, catalog) {
  if (!rawName || !catalog?.length) return true; // Don't warn if catalog still loading
  const n = _normalize(rawName);
  if (!n) return true;
  return catalog.some((p) => {
    const cn = _normalize(p.name);
    if (!cn) return false;
    return n.includes(cn) || cn.includes(n);
  });
}

const EMPTY_MANUAL = {
  patient_name: "", procedure_name: "",
  surgeon_id: "", assistant_surgeon_id: "", assistant_surgeon_2_id: "", anesthesiologist_id: "",
  period: "manha", time: "", bed: "", specialty: "C. Geral",
  cns: "", birth_date: "", sex: "",
};

function FilteredMapDetail({ id }) {
  const nav = useNavigate();
  const { user } = useAuth();
  const [map, setMap] = useState(null);
  const [rows, setRows] = useState([]);
  const [surgeons, setSurgeons] = useState([]);
  const [anesths, setAnesths] = useState([]);
  const [procedures, setProcedures] = useState([]);
  const [saving, setSaving] = useState(false);
  const [addingSurgeon, setAddingSurgeon] = useState(null);
  const [refiltering, setRefiltering] = useState(false);
  const [promptedPending, setPromptedPending] = useState(null);
  const [suspension, setSuspension] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [manual, setManual] = useState(EMPTY_MANUAL);
  const [addingManual, setAddingManual] = useState(false);
  const [metaEdit, setMetaEdit] = useState(null);
  const [savingMeta, setSavingMeta] = useState(false);
  const [dirty, setDirty] = useState(false);

  async function reload() {
    const r = await api.get(`/filtered-maps/${id}`);
    setMap(r.data);
    setRows(r.data.rows || []);
    setDirty(false);
  }

  useEffect(() => {
    reload();
    api.get("/surgeons").then((r) => setSurgeons(r.data));
    api.get("/anesthesiologists").then((r) => setAnesths(r.data));
    api.get("/procedures").then((r) => setProcedures(r.data || []));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Auto-prompt to register pending non-team surgeons
  useEffect(() => {
    const pending = map?.stats?.pending_surgeons || [];
    if (!pending.length) return;
    const key = pending.map((p) => p.name).sort().join("|");
    if (promptedPending === key) return;
    setPromptedPending(key);
    const list = pending.map((p) => `  • ${p.name} (${p.occurrences}x)`).join("\n");
    (async () => {
      const ok = await confirm({
        description:
          `Foram encontrados ${pending.length} cirurgião(ões) novo(s) no mapa, ainda não cadastrados:\n\n${list}\n\nCadastrar todos como NÃO-EQUIPE agora?\n(Você pode editar ou excluir depois em "Cirurgiões".)`,
      });
      if (ok) bulkRegisterPending();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map?.stats?.pending_surgeons]);

  function updateRow(i, patch) {
    setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
    setDirty(true);
  }

  async function saveRows() {
    setSaving(true);
    try {
      await api.patch(`/filtered-maps/${id}`, { rows });
      setDirty(false);
      toast.success("Alterações salvas");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  }

  // CRÍTICO: edições de cirurgião/anestesista/procedimento vivem no estado
  // local até "Salvar". Toda ação que gera/confirma casos PRECISA persistir
  // as linhas antes, senão os casos saem com a equipe ANTIGA.
  async function persistRowsIfDirty() {
    if (!dirty) return true;
    try {
      await api.patch(`/filtered-maps/${id}`, { rows });
      setDirty(false);
      return true;
    } catch (e) {
      toast.error("Falha ao salvar as edições do mapa: " + formatApiError(e.response?.data?.detail));
      return false;
    }
  }

  async function regenerateCases() {
    if (!(await confirm({
      description: "REGENERAR vai APAGAR todos os casos já criados deste mapa, reaplicar o filtro mais recente " +
        "(desmembramento de bilaterais, correção de typos, etc.) e recriar os casos.\n\n" +
        "Útil quando o mapa foi gerado antes de uma correção do sistema.\n\n" +
        "Deseja continuar?",
    }))) return;
    if (!(await persistRowsIfDirty())) return;
    try {
      const r = await api.post(`/filtered-maps/${id}/regenerate-cases`);
      const d = r.data || {};
      toast.success(
        `Regenerado: ${d.deleted_cases || 0} caso(s) apagado(s) → ${d.generate?.created || 0} recriado(s). ` +
        `Mapa: ${d.refilter?.matched_rows || 0} linha(s) ativas.`,
      );
      await reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }

  async function generateCases() {
    if (!(await confirm({ description: "Gerar casos cirúrgicos para todas as linhas incluídas?" }))) return;
    if (!(await persistRowsIfDirty())) return;
    try {
      const r = await api.post(`/filtered-maps/${id}/generate-cases`);
      const { created, already_existed, synced = 0, replaced = 0, replaced_other_maps = 0, alerts = [] } = r.data;
      toast.success(
        `${created} caso(s) criado(s), ${synced} atualizado(s) com os dados do mapa (${already_existed} já existiam` +
        (replaced > 0 ? `, ${replaced} obsoleto(s) substituído(s)` : "") +
        (replaced_other_maps > 0 ? `, ${replaced_other_maps} duplicado(s) de outros mapas do mesmo dia removido(s)` : "") + `).`,
      );
      const problematic = alerts.filter(
        (a) => (a.unrecognized || []).length > 0 || (a.ambiguous || []).length > 0,
      );
      if (problematic.length > 0) {
        const preview = problematic.slice(0, 5).map((a) => {
          const unreco = (a.unrecognized || []).length ? `não cadastrado: ${(a.unrecognized || []).join(", ")}` : "";
          const ambig = (a.ambiguous || []).length
            ? `ambíguo: ${(a.ambiguous || []).map((x) => `${x.token} → [${x.candidates.map((c) => c.name).join(" | ")}]`).join("; ")}`
            : "";
          return `• ${a.patient_name || "paciente"}: ${[unreco, ambig].filter(Boolean).join(" / ")}`;
        }).join("\n");
        const more = problematic.length > 5 ? `\n... e mais ${problematic.length - 5} caso(s).` : "";
        toast.warning(
          `${problematic.length} caso(s) precisam de atenção — procedimento não cadastrado ou ambíguo.`,
          { duration: 12000, description: preview + more },
        );
      }
      nav("/casos");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }

  async function addAsTeamSurgeon(fullName) {
    setAddingSurgeon(fullName);
    try {
      await api.post("/surgeons", {
        full_name: fullName,
        is_team_member: true,
        active: true,
        specialty: "Cirurgia Geral",
      });
      toast.success(`"${fullName}" cadastrado como equipe. Reaplicando filtro...`);
      const r = await api.post(`/filtered-maps/${id}/refilter`);
      setMap(r.data);
      setRows(r.data.rows || []);
      toast.success(`Mapa atualizado: ${r.data.stats?.matched_rows || 0} cirurgias identificadas como da equipe.`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setAddingSurgeon(null); }
  }

  async function promoteToTeam(surgeonId, existingName, rawName) {
    setAddingSurgeon(surgeonId);
    try {
      await api.patch(`/surgeons/${surgeonId}`, { is_team_member: true });
      if (rawName && existingName && rawName.trim().toLowerCase() !== existingName.trim().toLowerCase()) {
        try { await api.post(`/surgeons/${surgeonId}/aliases`, { alias: rawName }); } catch (_e) { /* alias optional */ }
      }
      toast.success(`"${existingName}" promovido(a) a equipe. Reaplicando filtro...`);
      const r = await api.post(`/filtered-maps/${id}/refilter`);
      setMap(r.data);
      setRows(r.data.rows || []);
      toast.success(`Mapa atualizado: ${r.data.stats?.matched_rows || 0} cirurgias identificadas.`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setAddingSurgeon(null); }
  }

  async function acceptProcedureVariation(procedureId, procedureName, rawName) {
    setAddingSurgeon(`proc-${procedureId}-${rawName}`);
    try {
      await api.post(`/procedures/${procedureId}/aliases`, { alias: rawName });
      toast.success(`"${rawName}" salvo como apelido de "${procedureName}". Reaplicando filtro...`);
      const r = await api.post(`/filtered-maps/${id}/refilter`);
      setMap(r.data);
      setRows(r.data.rows || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setAddingSurgeon(null); }
  }

  async function rejectProcedureVariation(procedureId, procedureName, rawName) {
    setAddingSurgeon(`proc-${procedureId}-${rawName}`);
    try {
      await api.post(`/procedures/variation-rejects`, { procedure_id: procedureId, raw_name: rawName });
      toast.success(`"${rawName}" recusado como apelido de "${procedureName}". Não será sugerido novamente.`);
      const r = await api.post(`/filtered-maps/${id}/refilter`);
      setMap(r.data);
      setRows(r.data.rows || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setAddingSurgeon(null); }
  }

  async function refilter() {
    setRefiltering(true);
    try {
      const r = await api.post(`/filtered-maps/${id}/refilter`);
      setMap(r.data);
      setRows(r.data.rows || []);
      toast.success(`Filtro reaplicado: ${r.data.stats?.matched_rows || 0} cirurgias identificadas.`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setRefiltering(false); }
  }

  async function addManualRow() {
    const patient = (manual.patient_name || "").trim();
    const proc = (manual.procedure_name || "").trim();
    if (!patient) { toast.error("Informe o nome do paciente."); return; }
    if (!proc) { toast.error("Informe o procedimento."); return; }
    setAddingManual(true);
    try {
      const surgeon = surgeons.find((s) => s.id === manual.surgeon_id);
      const assist = surgeons.find((s) => s.id === manual.assistant_surgeon_id);
      const assist2 = surgeons.find((s) => s.id === manual.assistant_surgeon_2_id);
      const anes = anesths.find((a) => a.id === manual.anesthesiologist_id);
      const body = {
        patient_name: patient,
        procedure_name: proc,
        period: manual.period,
        time: manual.time || null,
        bed: manual.bed || null,
        specialty: manual.specialty || null,
        cns: manual.cns || null,
        birth_date: manual.birth_date || null,
        sex: manual.sex || null,
        surgeon_id: surgeon?.id || null,
        surgeon_name: surgeon?.full_name || null,
        assistant_surgeon_id: assist?.id || null,
        assistant_surgeon_name: assist?.full_name || null,
        assistant_surgeon_2_id: assist2?.id || null,
        assistant_surgeon_2_name: assist2?.full_name || null,
        anesthesiologist_id: anes?.id || null,
        anesthesiologist_name: anes?.full_name || null,
      };
      await api.post(`/filtered-maps/${id}/rows`, body);
      toast.success("Linha adicionada manualmente.");
      await reload();
      setManualOpen(false);
      setManual(EMPTY_MANUAL);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setAddingManual(false); }
  }

  async function registerSurgeonInline(rowIdx, fullName, role) {
    if (!fullName) return;
    if (!(await confirm({ description: `Cadastrar "${fullName}" como cirurgião não-equipe e vincular a este caso?` }))) return;
    try {
      const r = await api.post("/surgeons", {
        full_name: fullName, is_team_member: false, active: true,
      });
      const created = r.data;
      setSurgeons((prev) => [...prev, created]);
      if (role === "primary") {
        updateRow(rowIdx, { surgeon_id: created.id, surgeon_name: created.full_name });
      } else {
        updateRow(rowIdx, { assistant_surgeon_id: created.id, assistant_surgeon_name: created.full_name });
      }
      toast.success(`${fullName} cadastrado como cirurgião (não-equipe).`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }

  async function bulkRegisterPending() {
    const pending = (map?.stats?.pending_surgeons || []).map((p) => p.name);
    if (!pending.length) return;
    const list = pending.map((n) => `  • ${n}`).join("\n");
    if (!(await confirm({ description: `Cadastrar os ${pending.length} cirurgião(ões) abaixo como NÃO-EQUIPE?\n\n${list}` }))) return;
    try {
      const r = await api.post("/surgeons/bulk-register", { names: pending, is_team_member: false });
      const created = r.data?.created || [];
      setSurgeons((prev) => [...prev, ...created]);
      toast.success(`${created.length} cirurgião(ões) cadastrado(s). Reaplicando o filtro…`);
      await refilter();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }

  async function confirmAlias(entityId, alias, entityType = "surgeon") {
    const endpoint = entityType === "anesthesiologist"
      ? `/anesthesiologists/${entityId}/aliases`
      : `/surgeons/${entityId}/aliases`;
    try {
      await api.post(endpoint, { alias });
      toast.success(`"${alias}" salvo como apelido. Reaplicando o filtro…`);
      await refilter();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }

  async function rejectNameMatch(entityId, entityType, rawName) {
    if (!(await confirm({
      description: `Confirmar que "${rawName}" NÃO é a mesma pessoa do cadastro?\n\nO sistema vai parar de sugerir essa associação nos próximos mapas.`,
    }))) return;
    try {
      await api.post(`/name-match-rejects`, {
        entity_id: entityId, entity_type: entityType, raw_name: rawName,
      });
      toast.success(`"${rawName}" não será mais sugerido como variação. Reaplicando o filtro…`);
      await refilter();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }

  async function rejectAsTeam(rawName) {
    if (!(await confirm({
      description: `Confirmar que "${rawName}" NÃO é da sua equipe?\n\nO sistema vai parar de:\n  • Sugerir esse nome como similar a alguém do cadastro\n  • Promover a equipe automaticamente\n  • Incluir linhas só por causa desse nome\n\nA ação é reversível em "Cirurgiões".`,
    }))) return;
    try {
      await api.post(`/name-match-rejects`, { raw_name: rawName });
      toast.success(`"${rawName}" marcado como não-equipe permanente. Reaplicando o filtro…`);
      await refilter();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }

  async function registerSingle(fullName) {
    if (!(await confirm({ description: `Cadastrar "${fullName}" como cirurgião não-equipe?` }))) return;
    try {
      await api.post("/surgeons/bulk-register", { names: [fullName], is_team_member: false });
      toast.success(`"${fullName}" cadastrado. Reaplicando o filtro…`);
      await refilter();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }

  if (!map) return <div className="text-slate-500">Carregando...</div>;

  async function onToggleExecuted(rowIdx, newValue) {
    const row = rows[rowIdx];
    if (newValue) {
      try {
        await api.patch(`/filtered-maps/${id}/rows/${row.order}/execution`, { executed: true });
        updateRow(rowIdx, { executed: true, suspension_reason: null, suspension_notes: null });
        toast.success("Marcada como executada");
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    } else {
      setSuspension({ row_idx: rowIdx, order: row.order, patient_name: row.patient_name, reason: "condicoes_clinicas", notes: "" });
    }
  }

  async function submitSuspension() {
    if (!suspension) return;
    if (suspension.reason === "outros" && !suspension.notes?.trim()) {
      toast.error("Informe o motivo em 'Outros'."); return;
    }
    try {
      await api.patch(`/filtered-maps/${id}/rows/${suspension.order}/execution`, {
        executed: false,
        suspension_reason: suspension.reason,
        suspension_notes: suspension.notes,
      });
      updateRow(suspension.row_idx, {
        executed: false,
        suspension_reason: suspension.reason,
        suspension_notes: suspension.notes,
      });
      toast.success("Cirurgia marcada como suspensa");
      setSuspension(null);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }

  async function deleteMap() {
    if (!(await confirm({ description: "Excluir este mapa filtrado? Todos os casos gerados a partir dele também serão removidos." }))) return;
    try {
      const r = await api.delete(`/filtered-maps/${id}`);
      toast.success(`Mapa excluído (${r.data.deleted_cases || 0} casos removidos).`);
      nav("/mapa-filtrado");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }

  async function unlockMap() {
    if (!(await confirm({
      description: "Destravar mapa confirmado?\n\n" +
        "• Você poderá editar linhas, anestesistas, cirurgiões, etc.\n" +
        "• Edits afetarão o faturamento e o painel imediatamente.\n" +
        "• A ação fica registrada no mapa (auditoria).\n\n" +
        "Após corrigir, lembre-se de reconfirmar o mapa.",
    }))) return;
    try {
      await api.post(`/filtered-maps/${id}/unlock`);
      toast.success("Mapa destravado. Pode editar e reconfirmar quando terminar.");
      await reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }

  async function saveMeta() {
    if (!metaEdit) return;
    if (!(await confirmIfFarDate(metaEdit.map_date, "data do mapa"))) return;
    setSavingMeta(true);
    try {
      await api.patch(`/filtered-maps/${id}`, {
        map_date: metaEdit.map_date || null,
        hospital_id: metaEdit.hospital_id || null,
        hospital_name: metaEdit.hospital_name || null,
        center: metaEdit.center || null,
      });
      toast.success("Mapa atualizado. Mapa bruto e casos vinculados também foram corrigidos.");
      setMetaEdit(null);
      await reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingMeta(false); }
  }

  async function deleteRow(row) {
    if (!(await confirm({ description: `Remover a linha de ${row.patient_name || "paciente"} do mapa? O caso vinculado (se houver) também será removido.` }))) return;
    try {
      await api.delete(`/filtered-maps/${id}/rows/${row.order}`);
      toast.success("Linha removida");
      await reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }

  async function confirmMap() {
    const pendingExec = rows.filter((r) => r.included && r.executed !== false).length;
    const suspended = rows.filter((r) => r.included && r.executed === false).length;
    if (!(await confirm({
      description: `Confirmar este mapa?\n\n✓ ${pendingExec} cirurgia(s) serão liberadas para Faturamento/Financeiro.\n✗ ${suspended} cirurgia(s) irão para "Suspensas".\n\nApós confirmar, o mapa fica bloqueado para alterações deste tipo.`,
    }))) return;
    if (!(await persistRowsIfDirty())) return;
    setConfirming(true);
    try {
      const r = await api.post(`/filtered-maps/${id}/confirm`);
      toast.success(`Mapa confirmado. ${r.data.confirmed_cases} caso(s) liberados; ${r.data.suspended_cases} suspenso(s).`);
      await reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setConfirming(false); }
  }

  function hoursSinceSurgery() {
    if (!map?.map_date) return 0;
    const sd = new Date(map.map_date + "T00:00:00");
    const now = new Date();
    return Math.max(0, (now - sd) / 36e5);
  }

  async function exportPdf() {
    try {
      await downloadFile(`/reports/filtered-map/${id}.pdf`, `mapa_filtrado_${map.map_date || ""}.pdf`);
      toast.success("PDF gerado.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }

  async function exportXlsx() {
    try {
      await downloadFile(`/reports/filtered-map/${id}.xlsx`, `mapa_filtrado_${map.map_date || ""}.xlsx`);
      toast.success("Excel gerado.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }

  const groups = { manha: [], tarde: [], noite: [] };
  rows.forEach((r, idx) => {
    const k = r.period || "manha";
    (groups[k] || groups.manha).push({ ...r, _idx: idx });
  });
  const stats = map.stats || {};

  return (
    <div className="space-y-5" data-testid="filtered-map-detail">
      <MapHeaderActions
        user={user}
        confirmed={!!map.confirmed_at}
        saving={saving}
        confirming={confirming}
        dirty={dirty}
        onBack={() => nav("/mapa-filtrado")}
        onPrint={() => window.print()}
        onExportPdf={exportPdf}
        onExportXlsx={exportXlsx}
        onSaveRows={saveRows}
        onAddManual={() => { setManual(EMPTY_MANUAL); setManualOpen(true); }}
        onEditMeta={() => setMetaEdit({
          map_date: map.map_date || "",
          hospital_id: map.hospital_id || null,
          hospital_name: map.hospital_name || "",
          center: map.center || "",
        })}
        onConfirmMap={confirmMap}
        onUnlockMap={unlockMap}
        onGenerateCases={generateCases}
        onRegenerateCases={regenerateCases}
        onDeleteMap={deleteMap}
      />

      {!map.confirmed_at && hoursSinceSurgery() >= 24 && (
        <div
          className="bg-amber-50 border border-amber-200 rounded-md px-4 py-2.5 text-sm text-amber-900 flex items-center gap-2 no-print"
          data-testid="awaiting-confirmation-banner"
        >
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span>
            <strong>Aguardando confirmação.</strong> Já se passaram {Math.floor(hoursSinceSurgery())}h desde a data da cirurgia.
            Desmarque as cirurgias que não foram realizadas e clique em <strong>"Confirmar mapa"</strong> para liberar no Faturamento/Financeiro.
          </span>
        </div>
      )}

      {stats.raw_rows_total !== undefined && (
        <div className="bg-blue-50 border border-blue-200 rounded-md px-4 py-2 text-sm text-blue-900 no-print">
          <strong>{stats.matched_rows}</strong> de {stats.raw_rows_total} linhas do mapa bruto foram identificadas como cirurgias da sua equipe
          {stats.unmatched_rows > 0 && <span className="text-blue-700"> · {stats.unmatched_rows} linhas descartadas</span>}.
        </div>
      )}

      <PendingTeamPromotions
        items={stats.pending_team_promotions}
        onPromote={promoteToTeam}
        onReject={rejectAsTeam}
        addingSurgeon={addingSurgeon}
      />
      <PendingProcedureVariations
        items={stats.pending_procedure_variations}
        onAccept={acceptProcedureVariation}
        onReject={rejectProcedureVariation}
        busyKey={addingSurgeon}
      />
      <AmbiguousMatches
        items={stats.ambiguous_matches}
        onConfirm={confirmAlias}
        onReject={rejectNameMatch}
      />
      <PendingSurgeons
        items={stats.pending_surgeons}
        onConfirm={confirmAlias}
        onRegisterSingle={registerSingle}
        onIgnore={rejectAsTeam}
        onBulkRegister={bulkRegisterPending}
      />

      <div className="bg-white rounded-md border border-slate-200 shadow-sm p-6 print:border-0 print:shadow-none">
        <div className="flex items-start justify-between border-b border-blue-700 pb-3 mb-5">
          <div>
            <img src="/brand/apex-logo.png" alt="APEX MEDICAL HUB" className="h-12 w-auto object-contain" />
          </div>
          <div className="text-right">
            <div className="font-display text-2xl font-bold text-slate-900">BV Serviços Médicos</div>
            <div className="font-display text-sm font-semibold text-slate-700">Mapa Cirúrgico</div>
            <div className="text-xs text-slate-500">{formatBRDate(map.map_date)}</div>
            <div className="text-xs text-slate-500">{map.hospital_name} {map.center ? ` - ${map.center}` : ""}</div>
            {map.hospital_name && !map.hospital_id && (
              <div className="text-[11px] text-amber-700 print:hidden" data-testid="fmap-unlinked-hospital">
                ⚠ hospital não cadastrado — produção por hospital ficará "sem hospital"
              </div>
            )}
          </div>
        </div>

        {["manha", "tarde", "noite"].map((p) => (
          <div key={p} className="mb-6">
            <div className={`inline-block px-3 py-1 rounded text-xs font-bold tracking-wider mb-2 period-${p}`}>
              {PERIOD_LABEL[p]}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs border border-slate-200 min-w-[900px]">
                <thead className="bg-slate-50 text-slate-700 font-semibold uppercase tracking-wider">
                  <tr>
                    <th className="border border-slate-200 px-2 py-1.5 text-left">N°</th>
                    <th className="border border-slate-200 px-2 py-1.5 text-left">Paciente</th>
                    <th className="border border-slate-200 px-2 py-1.5 text-left">CNS</th>
                    <th className="border border-slate-200 px-2 py-1.5 text-left">DN</th>
                    <th className="border border-slate-200 px-2 py-1.5 text-left">S</th>
                    <th className="border border-slate-200 px-2 py-1.5 text-left">Leito</th>
                    <th className="border border-slate-200 px-2 py-1.5 text-left">Hora</th>
                    <th className="border border-slate-200 px-2 py-1.5 text-left">Cirurgia</th>
                    <th className="border border-slate-200 px-2 py-1.5 text-left">Cirurgião</th>
                    <th className="border border-slate-200 px-2 py-1.5 text-left">Aux.</th>
                    <th className="border border-slate-200 px-2 py-1.5 text-left">Aux. 2</th>
                    <th className="border border-slate-200 px-2 py-1.5 text-left">Anestesista</th>
                    <th className="border border-slate-200 px-2 py-1.5 no-print">Executada?</th>
                    <th className="border border-slate-200 px-2 py-1.5 no-print" title="Cirurgia pro bono — conta na produção (total + pro bono) mas NÃO gera faturamento nem efeito financeiro.">
                      Pro Bono?
                    </th>
                    <th className="border border-slate-200 px-2 py-1.5 no-print">Incluir</th>
                  </tr>
                </thead>
                <tbody>
                  {groups[p].length === 0 && <tr><td colSpan={15} className="text-center p-4 text-slate-400">—</td></tr>}
                  {groups[p].map((row) => {
                    const isUnrecognized = !!row.procedure_name && !procedureExistsInCatalog(row.procedure_name, procedures);
                    return (
                    <tr key={row._idx} className={`${row.included ? "" : "opacity-40 line-through"} ${row.pro_bono ? "bg-emerald-50/50" : ""}`} data-testid={`fmap-detail-row-${row.order}`}>
                      <td className="border border-slate-200 px-2 py-1.5">{row.order}</td>
                      <td className="border border-slate-200 px-1 py-1">
                        <input
                          type="text"
                          className="w-full bg-transparent font-medium px-1 py-0.5 rounded border border-transparent hover:border-slate-300 focus:border-blue-500 focus:bg-white focus:outline-none"
                          value={row.patient_name || ""}
                          onChange={(e) => updateRow(row._idx, { patient_name: e.target.value })}
                          data-testid={`input-patient-${row._idx}`}
                          title="Editar nome do paciente"
                        />
                      </td>
                      <td className="border border-slate-200 px-1 py-1">
                        <input
                          type="text"
                          className="w-full bg-transparent font-mono text-xs px-1 py-0.5 rounded border border-transparent hover:border-slate-300 focus:border-blue-500 focus:bg-white focus:outline-none"
                          value={row.cns || ""}
                          onChange={(e) => updateRow(row._idx, { cns: e.target.value })}
                          data-testid={`input-cns-${row._idx}`}
                          title="Editar CNS"
                        />
                      </td>
                      <td className="border border-slate-200 px-1 py-1">
                        <input
                          type="date"
                          className="w-full bg-transparent text-xs px-1 py-0.5 rounded border border-transparent hover:border-slate-300 focus:border-blue-500 focus:bg-white focus:outline-none"
                          value={(row.birth_date || "").substring(0, 10)}
                          onChange={(e) => updateRow(row._idx, { birth_date: e.target.value })}
                          data-testid={`input-dob-${row._idx}`}
                          title="Data de nascimento"
                        />
                      </td>
                      <td className="border border-slate-200 px-1 py-1">
                        <select
                          className="w-full bg-transparent text-xs px-1 py-0.5 rounded border border-transparent hover:border-slate-300 focus:border-blue-500 focus:bg-white focus:outline-none"
                          value={row.sex || ""}
                          onChange={(e) => updateRow(row._idx, { sex: e.target.value })}
                          data-testid={`input-sex-${row._idx}`}
                          title="Sexo"
                        >
                          <option value="">—</option>
                          <option value="M">M</option>
                          <option value="F">F</option>
                        </select>
                      </td>
                      <td className="border border-slate-200 px-1 py-1">
                        <input
                          type="text"
                          className="w-full bg-transparent text-xs px-1 py-0.5 rounded border border-transparent hover:border-slate-300 focus:border-blue-500 focus:bg-white focus:outline-none"
                          value={row.bed || ""}
                          onChange={(e) => updateRow(row._idx, { bed: e.target.value })}
                          data-testid={`input-bed-${row._idx}`}
                          title="Leito"
                        />
                      </td>
                      <td className="border border-slate-200 px-1 py-1">
                        <input
                          type="text"
                          placeholder="HH:MM"
                          className="w-full bg-transparent text-xs px-1 py-0.5 rounded border border-transparent hover:border-slate-300 focus:border-blue-500 focus:bg-white focus:outline-none"
                          value={row.time || ""}
                          onChange={(e) => updateRow(row._idx, { time: e.target.value })}
                          data-testid={`input-time-${row._idx}`}
                          title="Hora"
                        />
                      </td>
                      <td className="border border-slate-200 px-1 py-1">
                        <div className="flex items-start gap-1.5">
                          <input
                            type="text"
                            className="flex-1 bg-transparent px-1 py-0.5 rounded border border-transparent hover:border-slate-300 focus:border-blue-500 focus:bg-white focus:outline-none"
                            value={row.procedure_name || ""}
                            onChange={(e) => updateRow(row._idx, { procedure_name: e.target.value })}
                            data-testid={`input-procedure-${row._idx}`}
                            title="Editar nome do procedimento"
                          />
                          {row.split_from_raw && (
                            <span
                              className="shrink-0 text-[9px] px-1.5 py-0.5 rounded bg-violet-100 text-violet-800 font-semibold uppercase tracking-wider cursor-help no-print"
                              title={`Desmembrado de: "${row.split_source || "—"}" (${(row.split_index ?? 0) + 1}/${row.split_total || "?"})`}
                              data-testid={`badge-split-${row.order}`}
                            >
                              desmembrado
                            </span>
                          )}
                          {row.manual_entry && (
                            <span
                              className="shrink-0 text-[9px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-semibold uppercase tracking-wider no-print"
                              title={`Adicionado manualmente por ${row.manual_entry_by_name || "—"}`}
                              data-testid={`badge-manual-${row.order}`}
                            >
                              manual
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="border border-slate-200 px-2 py-1.5">
                        <div className="flex items-center gap-1">
                          <select className="w-full text-xs bg-transparent no-print-select"
                            value={row.surgeon_id || ""}
                            onChange={(e) => {
                              if (e.target.value === "") {
                                updateRow(row._idx, { surgeon_id: null }); return;
                              }
                              const s = surgeons.find((x) => x.id === e.target.value);
                              updateRow(row._idx, { surgeon_id: s?.id || null, surgeon_name: s?.full_name || "" });
                            }}
                            data-testid={`select-surgeon-${row._idx}`}
                          >
                            <option value="">{row.surgeon_id ? "— remover —" : (row.surgeon_name ? `detectado: ${row.surgeon_name}` : "— selecionar —")}</option>
                            <optgroup label="Equipe">
                              {surgeons.filter((s) => s.is_team_member).map((s) => <option key={s.id} value={s.id}>{s.full_name}</option>)}
                            </optgroup>
                            <optgroup label="Não-equipe">
                              {surgeons.filter((s) => !s.is_team_member).map((s) => <option key={s.id} value={s.id}>{s.full_name}</option>)}
                            </optgroup>
                          </select>
                          {!row.surgeon_id && row.surgeon_name && (
                            <button
                              type="button"
                              className="text-[10px] text-blue-700 hover:underline whitespace-nowrap no-print shrink-0"
                              onClick={() => registerSurgeonInline(row._idx, row.surgeon_name, "primary")}
                              data-testid={`register-surg-${row._idx}`}
                              title={`Cadastrar "${row.surgeon_name}" como novo cirurgião`}
                            >+cadastrar</button>
                          )}
                        </div>
                      </td>
                      <td className="border border-slate-200 px-2 py-1.5">
                        <div className="flex items-center gap-1">
                          <select className="w-full text-xs bg-transparent"
                            value={row.assistant_surgeon_id || ""}
                            onChange={(e) => {
                              if (e.target.value === "") {
                                updateRow(row._idx, { assistant_surgeon_id: null }); return;
                              }
                              const s = surgeons.find((x) => x.id === e.target.value);
                              updateRow(row._idx, { assistant_surgeon_id: s?.id || null, assistant_surgeon_name: s?.full_name || "" });
                            }}
                            data-testid={`select-assistant-${row._idx}`}
                          >
                            <option value="">{row.assistant_surgeon_id ? "— remover —" : (row.assistant_surgeon_name ? `detectado: ${row.assistant_surgeon_name}` : "— selecionar —")}</option>
                            <optgroup label="Equipe">
                              {surgeons.filter((s) => s.is_team_member).map((s) => <option key={s.id} value={s.id}>{s.full_name}</option>)}
                            </optgroup>
                            <optgroup label="Não-equipe">
                              {surgeons.filter((s) => !s.is_team_member).map((s) => <option key={s.id} value={s.id}>{s.full_name}</option>)}
                            </optgroup>
                          </select>
                          {!row.assistant_surgeon_id && row.assistant_surgeon_name && (
                            <button
                              type="button"
                              className="text-[10px] text-blue-700 hover:underline whitespace-nowrap no-print shrink-0"
                              onClick={() => registerSurgeonInline(row._idx, row.assistant_surgeon_name, "assistant")}
                              data-testid={`register-aux-${row._idx}`}
                              title={`Cadastrar "${row.assistant_surgeon_name}" como novo cirurgião`}
                            >+cadastrar</button>
                          )}
                        </div>
                      </td>
                      <td className="border border-slate-200 px-2 py-1.5">
                        <select className="w-full text-xs bg-transparent"
                          value={row.assistant_surgeon_2_id || ""}
                          onChange={(e) => {
                            if (e.target.value === "") {
                              updateRow(row._idx, { assistant_surgeon_2_id: null, assistant_surgeon_2_name: null }); return;
                            }
                            const s = surgeons.find((x) => x.id === e.target.value);
                            updateRow(row._idx, { assistant_surgeon_2_id: s?.id || null, assistant_surgeon_2_name: s?.full_name || "" });
                          }}
                          data-testid={`select-assistant2-${row._idx}`}
                          title="3º cirurgião (opcional). Quando preenchido, o valor líquido é dividido em 3 partes iguais."
                        >
                          <option value="">{row.assistant_surgeon_2_id ? "— remover —" : "— selecionar —"}</option>
                          <optgroup label="Equipe">
                            {surgeons.filter((s) => s.is_team_member).map((s) => <option key={s.id} value={s.id}>{s.full_name}</option>)}
                          </optgroup>
                          <optgroup label="Não-equipe">
                            {surgeons.filter((s) => !s.is_team_member).map((s) => <option key={s.id} value={s.id}>{s.full_name}</option>)}
                          </optgroup>
                        </select>
                      </td>
                      <td className="border border-slate-200 px-2 py-1.5">
                        <select className="w-full text-xs bg-transparent"
                          value={row.anesthesiologist_id || ""}
                          onChange={(e) => {
                            const s = anesths.find((x) => x.id === e.target.value);
                            updateRow(row._idx, { anesthesiologist_id: s?.id || null, anesthesiologist_name: s?.full_name || "" });
                          }}>
                          <option value="">—</option>
                          {anesths.map((s) => <option key={s.id} value={s.id}>{s.full_name}</option>)}
                        </select>
                      </td>
                      <td className="border border-slate-200 px-2 py-1.5 text-center no-print">
                        <label className="inline-flex items-center gap-1 cursor-pointer" title={row.executed === false ? `Suspensa: ${row.suspension_reason || "—"}` : "Executada"}>
                          <input
                            type="checkbox"
                            checked={row.executed !== false}
                            disabled={!!map.confirmed_at && user?.role !== "admin" && user?.role !== "admin_master"}
                            onChange={(e) => onToggleExecuted(row._idx, e.target.checked)}
                            data-testid={`executed-${row._idx}`}
                          />
                          {row.executed === false && <span className="text-[10px] text-red-600 font-semibold">SUSP</span>}
                        </label>
                      </td>
                      <td className="border border-slate-200 px-2 py-1.5 text-center no-print">
                        <label className="inline-flex items-center gap-1 cursor-pointer" title={
                          row.pro_bono
                            ? "Pro Bono — conta na produção mas NÃO gera faturamento"
                            : (isUnrecognized
                              ? "💡 Sugestão: este procedimento não está no cadastro. Marque como pro bono para contar como produção sem entrar no financeiro."
                              : "Marcar esta cirurgia como pro bono (sem efeito financeiro)")
                        }>
                          <input
                            type="checkbox"
                            checked={!!row.pro_bono}
                            onChange={(e) => updateRow(row._idx, { pro_bono: e.target.checked })}
                            data-testid={`pro-bono-${row._idx}`}
                          />
                          {row.pro_bono && <span className="text-[10px] text-emerald-700 font-semibold">PRO</span>}
                          {!row.pro_bono && isUnrecognized && (
                            <span className="text-[10px] text-amber-600" title="Procedimento não cadastrado — considere marcar como pro bono">💡</span>
                          )}
                        </label>
                      </td>
                      <td className="border border-slate-200 px-2 py-1.5 text-center no-print">
                        <div className="flex items-center justify-center gap-1">
                          <input
                            type="checkbox"
                            checked={!!row.included}
                            onChange={(e) => updateRow(row._idx, { included: e.target.checked })}
                            title="Incluir"
                            data-testid={`include-${row._idx}`}
                          />
                          <button
                            onClick={() => deleteRow(row)}
                            className="text-red-600 hover:bg-red-50 rounded p-0.5"
                            title="Remover linha"
                            data-testid={`delete-row-${row._idx}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ))}

        <div className="text-[10px] text-slate-500 border-t border-slate-200 pt-2">
          Obs.: Todos os pacientes do mapa cirúrgico que encontram-se no HLI, HC, HM devem estar presentes no HGR às 06h (matutino) ou às 12h (vespertino).
        </div>
      </div>

      <DiscardedRowsSection
        map={map}
        stats={stats}
        refiltering={refiltering}
        addingSurgeon={addingSurgeon}
        onRefilter={refilter}
        onAddAsTeamSurgeon={addAsTeamSurgeon}
        onPromoteToTeam={promoteToTeam}
      />

      <SuspensionDialog
        state={suspension}
        onChange={setSuspension}
        onClose={() => setSuspension(null)}
        onSubmit={submitSuspension}
      />
      <ManualRowDialog
        open={manualOpen}
        value={manual}
        onChange={setManual}
        onClose={() => setManualOpen(false)}
        onSubmit={addManualRow}
        loading={addingManual}
        surgeons={surgeons}
        anesths={anesths}
      />
      <MetaEditDialog
        value={metaEdit}
        onChange={setMetaEdit}
        onClose={() => setMetaEdit(null)}
        onSave={saveMeta}
        loading={savingMeta}
      />
    </div>
  );
}

export default function FilteredMapPage() {
  const params = useParams();
  if (params.id) return <FilteredMapDetail id={params.id} />;
  return <FilteredMapList />;
}
